import { useNavigate } from "@tanstack/react-router";
import { operacionDisplay, operacionLabel, tipoPlural } from "@/lib/seo";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTrackAnuncioView } from "@/hooks/use-track-anuncio-view";
import { ChevronLeft, ChevronRight, MapPin, Globe, X, Check, Loader2, Phone } from "lucide-react";

import { getAnuncioPublico } from "@/lib/anuncios.functions";
import { createSolicitudDesdeAnuncio, getSolicitudConectorEstado, cancelarSolicitudConector, updateSolicitudMensajeConector } from "@/lib/solicitudes.functions";
import { leerEmailConectorLight } from "@/lib/light-contacto";

type NotifResult = {
  notificable: boolean;
  anuncianteSinEmail: boolean;
  anuncianteTelefono: string | null;
  anuncianteNombre: string | null;
};
import { getMyActiveDemandaId } from "@/lib/conector.functions";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toggleDescartado } from "@/lib/interacciones.functions";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BackToPortal } from "@/components/portal/BackToPortal";
import { AuthDialog } from "@/components/site/AuthDialog";
import { useSessionUserId } from "@/hooks/use-portal-identity";
import { supabase } from "@/integrations/supabase/client";
import { formatPrice } from "@/lib/utils";
import { buildGalleryItems, GallerySlide, GalleryThumb, type GalleryItem } from "@/components/portal/GalleryMedia";

// ─── Helpers (compartidos con la ruta /local/$id) ────────────
export function priceForOp(anuncio: any, opSlug: string | null | undefined): { value: number | null; label: string } {
  const s = (opSlug ?? "").toLowerCase();
  if (s === "traspaso") return { value: anuncio.precio_traspaso ?? null, label: "Traspaso" };
  if (s === "alquiler") return { value: anuncio.precio_alquiler ?? null, label: "Alquiler" };
  if (s === "venta" || s === "rentabilidad") return { value: anuncio.precio_venta ?? null, label: "Venta" };
  // fallback preferente
  if (anuncio.precio_traspaso != null) return { value: anuncio.precio_traspaso, label: "Traspaso" };
  if (anuncio.precio_venta != null) return { value: anuncio.precio_venta, label: "Venta" };
  if (anuncio.precio_alquiler != null) return { value: anuncio.precio_alquiler, label: "Alquiler" };
  return { value: null, label: "" };
}

export function formatPriceEs(n: number | null | undefined, opSlug?: string | null): string | null {
  if (n == null || !Number.isFinite(n)) return null;
  const base = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n) + " €";
  return opSlug === "alquiler" ? base + "/mes" : base;
}

export function truncate(s: string, max: number) {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).replace(/\s+\S*$/, "") + "…";
}

export function buildSeoTitleAndH1(b: any) {
  const opSlug = b.operacion?.slug ?? null;
  const opDisplay = operacionDisplay(opSlug ?? "") ?? "Local";
  const tipoNombre = b.uso?.nombre ?? b.tipo_local?.nombre ?? "Local";
  const distrito = b.barrio?.nombre ?? null;
  const ciudad = b.ciudad?.nombre ?? null;
  const locBits = [distrito, ciudad].filter(Boolean).join(", ");
  const { value: precio } = priceForOp(b.anuncio, opSlug);
  const precioFmt = formatPriceEs(precio, opSlug);

  const h1Base = `${opDisplay} de ${tipoNombre.toLowerCase()}${locBits ? ` en ${locBits}` : ""}`;
  const titleBase = `${h1Base}${precioFmt ? ` — ${precioFmt}` : ""} | Bizaclia`;
  const title = titleBase.length > 65 ? truncate(titleBase.replace(" | Bizaclia", ""), 55) + " | Bizaclia" : titleBase;
  return { title, h1: h1Base, precioFmt, precio, opSlug, opDisplay, tipoNombre, ciudad, distrito, provincia: b.provincia?.nombre ?? null };
}

export function buildFacetPath(opSlug: string | null, usoSlug: string | null, provSlug: string | null, ciudadSlug: string | null, barrioSlug: string | null): string {
  const segs = [opSlug || "todas", usoSlug || "todas", provSlug || "todas", ciudadSlug || "todas", barrioSlug || "todas"];
  while (segs.length && segs[segs.length - 1] === "todas") segs.pop();
  return segs.length ? `/portal/${segs.join("/")}` : "/portal";
}

// ─── Related Links Block ─────────────────────────────────────
function RelatedLinksBlock({ bundle }: { bundle: any }) {
  const op = bundle.operacion;
  const uso = bundle.uso;
  const tipo = bundle.tipo_local;
  const prov = bundle.provincia;
  const ciudad = bundle.ciudad;
  const barrio = bundle.barrio;
  const opDisplay = operacionDisplay(op?.slug ?? "") ?? "Anuncios";
  const plural = tipoPlural(uso?.slug ?? uso?.nombre ?? tipo?.nombre ?? null);
  const ciudadNombre = ciudad?.nombre ?? "España";

  const facetLinks: Array<{ label: string; to: string }> = [];
  if (op?.slug && uso?.slug && prov?.slug && ciudad?.slug && barrio?.slug) {
    facetLinks.push({
      label: `Más ${plural} en ${opDisplay.toLowerCase()} en ${barrio.nombre}`,
      to: buildFacetPath(op.slug, uso.slug, prov.slug, ciudad.slug, barrio.slug),
    });
  }
  if (op?.slug && uso?.slug && prov?.slug && ciudad?.slug) {
    facetLinks.push({
      label: `${opDisplay} de ${plural} en ${ciudadNombre}`,
      to: buildFacetPath(op.slug, uso.slug, prov.slug, ciudad.slug, null),
    });
  }
  if (uso?.slug && prov?.slug && ciudad?.slug) {
    facetLinks.push({
      label: `Todos los ${plural} en ${ciudadNombre}`,
      to: buildFacetPath(null, uso.slug, prov.slug, ciudad.slug, null),
    });
  }
  if (op?.slug && ciudad?.slug && prov?.slug) {
    facetLinks.push({
      label: `${opDisplay} de locales y negocios en ${ciudadNombre}`,
      to: buildFacetPath(op.slug, null, prov.slug, ciudad.slug, null),
    });
  }
  if (op?.slug) {
    facetLinks.push({
      label: `${opDisplay} de locales y negocios en España`,
      to: buildFacetPath(op.slug, null, null, null, null),
    });
  }

  if (facetLinks.length === 0 && bundle.related.length === 0) return null;

  return (
    <section className="mt-8 space-y-6" aria-labelledby="mas-anuncios">
      <h2 id="mas-anuncios" className="text-lg font-semibold">
        Más {plural} en {opDisplay.toLowerCase()} en {ciudadNombre}
      </h2>
      {facetLinks.length > 0 && (
        <ul className="flex flex-wrap gap-2 text-sm">
          {facetLinks.map((l) => (
            <li key={l.to}>
              <a
                href={l.to}
                className="inline-flex items-center rounded-md border bg-muted/40 px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      )}
      {bundle.related.length > 0 && (
        <ul className="grid gap-3 sm:grid-cols-2">
          {bundle.related.map((r: any) => {
            const opD = operacionDisplay(r.operacion_slug ?? "") ?? "Anuncio";
            const tipoN = r.uso_nombre ?? r.tipo_nombre ?? "Local";
            const loc = [r.barrio_nombre, r.ciudad_nombre].filter(Boolean).join(", ");
            const precio = formatPriceEs(r.precio ?? null, r.operacion_slug);
            const title = `${opD} de ${tipoN.toLowerCase()}${loc ? ` en ${loc}` : ""}`;
            return (
              <li key={r.id}>
                <a
                  href={`/local/${r.id}`}
                  className="block rounded-lg border p-3 text-sm hover:bg-muted/40 transition-colors"
                >
                  <div className="font-medium truncate">{title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {precio ?? "Consultar precio"}
                    {r.referencia ? ` · Ref. ${r.referencia}` : ""}
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}


// ─── Inactive State ──────────────────────────────────────────
function InactiveState({ bundle }: { bundle: any }) {
  const opDisplay = operacionDisplay(bundle.operacion?.slug ?? "") ?? "Local";
  const tipoNombre = bundle.uso?.nombre ?? bundle.tipo_local?.nombre ?? "Local";
  const loc = [bundle.barrio?.nombre, bundle.ciudad?.nombre].filter(Boolean).join(", ");
  return (
    <main className="container mx-auto max-w-4xl px-4 sm:px-6 py-10 space-y-6">
      <div className="rounded-xl border bg-muted/30 p-6 space-y-2">
        <h1 className="text-2xl font-semibold">Este anuncio ya no está disponible</h1>
        <p className="text-muted-foreground">
          {opDisplay} de {tipoNombre.toLowerCase()}{loc ? ` en ${loc}` : ""} · Ref. {bundle.anuncio.referencia ?? "—"}
        </p>
        <p className="text-sm text-muted-foreground">
          Puede que haya sido retirado, vendido o esté fuera del mercado. A continuación tienes anuncios similares en activo.
        </p>
      </div>
      <RelatedLinksBlock bundle={bundle} />
    </main>
  );
}


// ─── Ficha reutilizable (página /local/$id y diálogos) ──────
export function AnuncioFicha({
  id,
  bundle,
  demandaSearch,
  trackView = true,
  showBackLink = true,
}: {
  id: string;
  bundle: any;
  demandaSearch?: string | undefined;
  trackView?: boolean;
  showBackLink?: boolean;
}) {
  const fnGet = useServerFn(getAnuncioPublico);
  const fnMyDemanda = useServerFn(getMyActiveDemandaId);

  useTrackAnuncioView(trackView ? id : null);

  // Si el anuncio está inactivo → estado ligero.
  if (bundle.status === "inactive") {
    return <InactiveState bundle={bundle} />;
  }

  const q = useQuery({
    queryKey: ["anuncio-publico", id],
    queryFn: () => fnGet({ data: { id } }),
  });

  const myDemandaQ = useQuery({
    queryKey: ["my-active-demanda"],
    queryFn: () => fnMyDemanda() as Promise<string | null>,
    enabled: !demandaSearch,
    staleTime: 60_000,
  });
  const demandaId = demandaSearch ?? myDemandaQ.data ?? null;


  const contactRef = useRef<HTMLDivElement>(null);
  const [stickyHidden, setStickyHidden] = useState(false);

  useEffect(() => {
    const el = contactRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      const visible = rect.top < window.innerHeight && rect.bottom > 0;
      setStickyHidden(visible);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [q.isSuccess]);

  if (q.isLoading) {
    return (
      <main className="container mx-auto max-w-6xl px-4 py-8 space-y-4">
        <div className="h-8 w-1/2 rounded bg-muted animate-pulse" />
        <div className="h-[420px] rounded-xl bg-muted animate-pulse" />
        <div className="h-40 rounded-xl bg-muted animate-pulse" />
      </main>
    );
  }
  if (q.error) throw q.error;
  if (!q.data) return null;

  const { anuncio, media, usos, operacion, tipo_local, ciudad, barrio, agencia } = q.data as any;
  const op = (operacion?.slug ?? "").toLowerCase();

  const galeria = buildGalleryItems(media);

  const titulo = `${usos[0]?.nombre ?? tipo_local?.nombre ?? "Local"}${operacion ? ` en ${operacion.nombre}` : ""}${barrio ? ` en ${barrio.nombre}, ${ciudad?.nombre ?? ""}` : ciudad ? ` en ${ciudad.nombre}` : ""}`;

  // H1: mismo formato construido para el título SEO.
  const seoMeta = buildSeoTitleAndH1(bundle);
  const h1 = seoMeta.h1;

  return (
    <main className="container mx-auto max-w-6xl px-4 sm:px-6 py-6 space-y-6 pb-32 sm:pb-6">
      {showBackLink && (
        <div>
          <BackToPortal to={demandaId ? `/conector/${demandaId}` : "/portal"} label={demandaId ? "Volver" : "Volver al portal"} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" />
        </div>
      )}

      <Gallery items={galeria} titulo={titulo} />

      <div className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold break-words">{h1}</h1>
        {anuncio.direccion_completa && anuncio.direccion_visible !== false && (
          <p className="text-muted-foreground flex items-center gap-1">
            <MapPin className="h-4 w-4" /> {anuncio.direccion_completa}
          </p>
        )}
        {!anuncio.direccion_completa && (barrio?.nombre || ciudad?.nombre) && (
          <p className="text-muted-foreground flex items-center gap-1">
            <MapPin className="h-4 w-4" /> {[barrio?.nombre, ciudad?.nombre].filter(Boolean).join(", ")}
          </p>
        )}
        {anuncio.referencia && (
          <p className="text-xs text-muted-foreground/70 font-medium tracking-wide">
            Ref. {anuncio.referencia}
          </p>
        )}
      </div>

      <PrecioBlock anuncio={anuncio} op={op} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-6">
          {anuncio.descripcion && (
            <Card>
              <CardContent className="p-6 space-y-2">
                <h2 className="text-lg font-semibold">Descripción</h2>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">{anuncio.descripcion}</p>
              </CardContent>
            </Card>
          )}

          <CaracteristicasBlock anuncio={anuncio} usos={usos} tipo_local={tipo_local} operacion={operacion} />

          {anuncio.distribucion && Array.isArray(anuncio.distribucion) && anuncio.distribucion.length > 0 && (
            <Card>
              <CardContent className="p-6 space-y-2">
                <h2 className="text-lg font-semibold">Distribución</h2>
                <ul className="text-sm divide-y">
                  {anuncio.distribucion.map((d: any, i: number) => (
                    <li key={i} className="flex justify-between py-2">
                      <span>{d.titulo}</span>
                      <span className="text-muted-foreground">{d.superficie ? `${d.superficie} m²` : "—"}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          {agencia && <AgenciaBlock agencia={agencia} />}
          <div ref={contactRef}>
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold">Contactar al anunciante</h2>
                  <p className="text-sm text-muted-foreground">Solicita más información o descarta este local.</p>
                </div>
                <ConectorActions demandaId={demandaId} anuncioId={anuncio.id} layout="stack" />
              </CardContent>
            </Card>
          </div>
        </aside>
      </div>

      <RelatedLinksBlock bundle={bundle} />

      <div className="sm:hidden">
        <ConectorStickyBar demandaId={demandaId} anuncioId={anuncio.id} hidden={stickyHidden} />
      </div>
    </main>

  );
}

function Gallery({ items, titulo }: { items: GalleryItem[]; titulo: string }) {
  const [idx, setIdx] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const touchStartX = useRef<number | null>(null);
  useEffect(() => { setIdx(0); }, [items.length]);

  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + items.length) % items.length);
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [fullscreen, items.length]);

  if (items.length === 0) {
    return (
      <div className="h-72 rounded-xl bg-muted flex items-center justify-center text-muted-foreground">
        Sin imágenes
      </div>
    );
  }

  const current = items[idx] ?? items[0];

  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    touchStartX.current = null;
    if (Math.abs(diff) < 50) return;
    if (diff > 0) next();
    else prev();
  };

  return (
    <div className="space-y-4">
      <div
        className="relative rounded-xl overflow-hidden bg-muted h-[320px] sm:h-[480px] cursor-zoom-in select-none"
        onClick={() => setFullscreen(true)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <GallerySlide
          item={current}
          alt={current.titulo ?? titulo}
          imgClassName="absolute inset-0 h-full w-full object-contain"
        />
        {items.length > 1 && (
          <>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              aria-label="Anterior"
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black rounded-full shadow-lg backdrop-blur-sm"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); next(); }}
              aria-label="Siguiente"
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black rounded-full shadow-lg backdrop-blur-sm"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div className="absolute bottom-3 right-3 rounded-full bg-black/60 text-white px-3 py-1 text-xs backdrop-blur-sm">
              {idx + 1} / {items.length}
            </div>
          </>
        )}
      </div>
      {items.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {items.map((m, i) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setIdx(i)}
              className={`relative shrink-0 h-20 w-28 rounded-lg overflow-hidden border-2 transition-all ${i === idx ? "border-primary ring-2 ring-primary/20" : "border-transparent opacity-60 hover:opacity-100"}`}
            >
              <GalleryThumb item={m} className="h-full w-full object-contain bg-muted" />
            </button>
          ))}
        </div>
      )}

      {fullscreen && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center select-none"
          onClick={() => setFullscreen(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <button
            type="button"
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-10"
            aria-label="Cerrar"
          >
            <X className="h-8 w-8" />
          </button>

          {items.length > 1 && (
            <>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                aria-label="Anterior"
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm z-10"
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); next(); }}
                aria-label="Siguiente"
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full backdrop-blur-sm z-10"
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            </>
          )}

          <div className={current.kind === "image" ? undefined : "relative aspect-video w-[90vw] max-w-6xl max-h-[85vh]"} onClick={(e) => e.stopPropagation()}>
            <GallerySlide
              item={current}
              alt={current.titulo ?? titulo}
              imgClassName="max-h-[90vh] max-w-[90vw] object-contain"
            />
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 text-white px-4 py-1 text-sm backdrop-blur-sm">
            {idx + 1} / {items.length}
          </div>
        </div>
      )}
    </div>
  );
}

function PrecioBlock({ anuncio, op }: { anuncio: any; op: string }) {
  const items: Array<{ label: string; valor: string }> = [];

  if (op === "venta") {
    if (anuncio.precio_venta != null) items.push({ label: "Venta", valor: `${formatPrice(anuncio.precio_venta)} €` });
  } else if (op === "traspaso") {
    if (anuncio.precio_traspaso != null) items.push({ label: "Traspaso", valor: `${formatPrice(anuncio.precio_traspaso)} €` });
    if (anuncio.precio_alquiler != null) items.push({ label: "Alquiler", valor: `${formatPrice(anuncio.precio_alquiler)} € / mes` });
  } else if (op === "alquiler") {
    if (anuncio.precio_alquiler != null) items.push({ label: "Alquiler", valor: `${formatPrice(anuncio.precio_alquiler)} € / mes` });
  } else if (op === "rentabilidad") {
    if (anuncio.precio_venta != null) items.push({ label: "Venta", valor: `${formatPrice(anuncio.precio_venta)} €` });
    if (anuncio.rent_bruta != null) items.push({ label: "Rentabilidad bruta", valor: `${anuncio.rent_bruta} %` });
  } else {
    if (anuncio.precio_venta != null) items.push({ label: "Venta", valor: `${formatPrice(anuncio.precio_venta)} €` });
    if (anuncio.precio_traspaso != null) items.push({ label: "Traspaso", valor: `${formatPrice(anuncio.precio_traspaso)} €` });
    if (anuncio.precio_alquiler != null) items.push({ label: "Alquiler", valor: `${formatPrice(anuncio.precio_alquiler)} € / mes` });
    if (anuncio.rent_bruta != null) items.push({ label: "Rentabilidad bruta", valor: `${anuncio.rent_bruta} %` });
  }

  if (items.length === 0) items.push({ label: "Precio", valor: "Consultar" });

  return (
    <div className="flex flex-wrap gap-x-8 gap-y-3">
      {items.map((it) => (
        <div key={it.label}>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{it.label}</div>
          <div className="text-xl font-bold">{it.valor}</div>
        </div>
      ))}
    </div>
  );
}

function CaracteristicasBlock({ anuncio, usos, tipo_local, operacion }: { anuncio: any; usos: any[]; tipo_local: any; operacion: any }) {
  const features: Array<{ label: string; value: string }> = [];
  if (anuncio.t_superficie != null) features.push({ label: "Superficie", value: `${anuncio.t_superficie} m²` });
  if (tipo_local) features.push({ label: "Tipo", value: tipo_local.nombre });
  if (operacion) features.push({ label: "Operación", value: operacion.nombre });
  if (anuncio.nivel_transito) features.push({ label: "Tránsito", value: String(anuncio.nivel_transito).replace(/_/g, " ") });
  if (anuncio.estado_inmueble) features.push({ label: "Estado", value: String(anuncio.estado_inmueble).replace(/_/g, " ") });
  if (anuncio.t_num_aseos != null) features.push({ label: "Aseos", value: String(anuncio.t_num_aseos) });
  if (anuncio.h_aforo != null) features.push({ label: "Aforo", value: String(anuncio.h_aforo) });
  if (anuncio.t_parking_plazas != null) features.push({ label: "Plazas parking", value: String(anuncio.t_parking_plazas) });
  if (anuncio.e_consumo) features.push({ label: "Consumo energético", value: String(anuncio.e_consumo).toUpperCase().replace(/_/g, "") });

  const bools: Array<[string, boolean]> = [
    ["A pie de calle", !!anuncio.a_pie_de_calle],
    ["Esquinero", !!anuncio.t_esquinero],
    ["Mucha luz", !!anuncio.t_mucha_luz],
    ["Amueblado", !!anuncio.t_amueblado],
    ["Aire acondicionado", !!anuncio.t_aire_acondicionado],
    ["Cocina", !!anuncio.t_cocina],
    ["Agua", !!anuncio.t_agua],
    ["Alarma", !!anuncio.t_alarma],
    ["Salida emergencia", !!anuncio.t_salida_emergencia],
    ["Acceso movilidad reducida", !!anuncio.t_acceso_movilidad_reducida],
    ["Salida de humos", !!anuncio.h_salida_humos],
    ["Cámara frigorífica", !!anuncio.h_camara_frigorifica],
    ["Obrador", !!anuncio.h_obrador],
    ["Terraza", !!anuncio.h_terraza],
    ["Parking", !!anuncio.t_parking],
    ["Muelle de carga", !!anuncio.n_muelle_carga],
    ["Vado", !!anuncio.n_vado],
  ].filter(([, v]) => v) as Array<[string, boolean]>;

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <h2 className="text-lg font-semibold">Características</h2>
        {bools.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {bools.map(([label]) => (
              <span key={label} className="inline-flex items-center rounded-md border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">
                {label}
              </span>
            ))}
          </div>
        )}
        {usos.length > 0 && (
          <div>
            <div className="text-xs text-muted-foreground mb-1">Uso actual</div>
            <div className="flex flex-wrap gap-1.5">
              {usos.map((u) => (
                <Badge key={u.id} variant="secondary">{u.nombre}</Badge>
              ))}
            </div>
          </div>
        )}
        {features.length > 0 && (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {features.map((f) => (
              <div key={f.label}>
                <dt className="text-xs text-muted-foreground">{f.label}</dt>
                <dd className="font-medium capitalize">{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function AgenciaBlock({ agencia }: { agencia: any }) {
  return (
    <Card>
      <CardContent className="p-6 space-y-3">
        <h2 className="text-lg font-semibold">Anunciado por</h2>
        <div className="flex flex-col items-start gap-2">
          {agencia.logo_url ? (
            <img src={agencia.logo_url} alt={`Logo de ${agencia.nombre}`} className="h-[50px] w-[150px] object-contain object-left rounded bg-background" />
          ) : (
            <div className="h-[50px] w-[150px] rounded bg-muted" />
          )}
          <div className="font-semibold text-base">{agencia.nombre}</div>
          {agencia.region && <div className="text-xs text-muted-foreground">{agencia.region}</div>}
          {agencia.descripcion_publica && (
            <p className="text-sm text-muted-foreground mt-1">{agencia.descripcion_publica}</p>
          )}
          <div className="mt-1 flex flex-wrap gap-3 text-sm">
            {agencia.url_web && (
              <a href={agencia.url_web} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                <Globe className="h-4 w-4" /> Web
              </a>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/** Clave de sessionStorage: anuncio del que se quería pedir información antes de entrar. */
const INTENCION_SOLICITUD = "bizaclia:solicitar-anuncio";

function ConectorActions({ demandaId, anuncioId, layout }: { demandaId: string | null; anuncioId: string; layout: "bar" | "stack" }) {
  const navigate = useNavigate();
  const fnCrear = useServerFn(createSolicitudDesdeAnuncio);
  const fnDescartar = useServerFn(toggleDescartado);
  const fnEstado = useServerFn(getSolicitudConectorEstado);
  const fnCancelar = useServerFn(cancelarSolicitudConector);
  const fnMensaje = useServerFn(updateSolicitudMensajeConector);

  const [authResolved, setAuthResolved] = useState(false);
  const userId = useSessionUserId();
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(() => { if (mounted) setAuthResolved(true); });
    return () => { mounted = false; };
  }, []);
  const [authOpen, setAuthOpen] = useState(false);

  const [mensajeOpen, setMensajeOpen] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [solicitudId, setSolicitudId] = useState<string | null>(null);
  const [notif, setNotif] = useState<NotifResult | null>(null);
  const [demandaVinculada, setDemandaVinculada] = useState<{ id: string; referencia: string | null } | null>(null);
  const [prefillEmail, setPrefillEmail] = useState<string | null>(null);

  const estadoQ = useQuery({
    queryKey: ["solicitud-conector-estado", demandaId, anuncioId],
    queryFn: () => fnEstado({ data: { demanda_id: demandaId, anuncio_id: anuncioId } }) as Promise<{ exists: boolean; id?: string; created_at?: string }>,
    enabled: !!userId,
  });
  const solicitado = !!estadoQ.data?.exists;
  const solicitadoAt = estadoQ.data?.created_at;
  const fechaFmt = solicitadoAt
    ? new Date(solicitadoAt).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })
    : null;

  const solicitarMut = useMutation({
    mutationFn: () =>
      fnCrear({ data: { anuncio_id: anuncioId, demanda_id: demandaId } }) as Promise<{
        ok: true;
        existing: boolean;
        id: string;
        notificacion: NotifResult | null;
        demanda: { id: string; referencia: string | null } | null;
      }>,
    onSuccess: (res) => {
      setDemandaVinculada(res.demanda);
      estadoQ.refetch();
      if (res.existing) {
        toast.success("Ya habías solicitado información de este local");
      } else {
        import("@/lib/tracking")
          .then((m) => m.trackConversion("generate_lead", { userId, listingId: anuncioId }))
          .catch(() => {});
        setSolicitudId(res.id);
        setMensaje("");
        setNotif(res.notificacion ?? null);
        setMensajeOpen(true);
      }
    },
    onError: (e: any) => toast.error(e?.message ?? "No se pudo crear la solicitud"),
  });

  const mensajeMut = useMutation({
    mutationFn: () =>
      fnMensaje({ data: { solicitud_id: solicitudId!, mensaje: mensaje.trim() } }) as Promise<{ ok: true }>,
    onSuccess: () => {
      toast.success("Detalles añadidos a la solicitud");
      setMensajeOpen(false);
    },
    onError: (e: any) => toast.error(e?.message ?? "No se pudieron guardar los detalles"),
  });

  const cancelarMut = useMutation({
    mutationFn: () =>
      fnCancelar({ data: { demanda_id: demandaId, anuncio_id: anuncioId } }) as Promise<{ ok: true }>,
    onSuccess: () => {
      toast.success("Solicitud cancelada");
      estadoQ.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "No se pudo cancelar la solicitud"),
  });

  const descartarMut = useMutation({
    mutationFn: () =>
      fnDescartar({ data: { anuncio_id: anuncioId, value: true } }) as Promise<{ descartado: boolean }>,
    onSuccess: () => {
      toast.message("Anuncio descartado");
      if (demandaId) navigate({ to: "/conector/{-$id}", params: { id: demandaId }, search: {} as never });
    },
    onError: (e: any) => toast.error(e?.message ?? "No se pudo descartar"),
  });


  const containerCls =
    layout === "bar"
      ? "flex flex-col sm:flex-row gap-2 sm:gap-3 sm:items-center sm:justify-center"
      : "flex flex-col gap-2";
  const btnCls = layout === "bar" ? "w-full sm:w-auto" : "w-full";

  const isLoggedOut = authResolved && !userId;

  // Sin sesión: se recuerda que quería pedir información y, en cuanto entra o
  // se registra, la solicitud se envía sola (sin volver a pulsar).
  const pedirAcceso = () => {
    try {
      sessionStorage.setItem(INTENCION_SOLICITUD, anuncioId);
    } catch {
      // sin almacenamiento: tendrá que volver a pulsar tras entrar
    }
    setPrefillEmail(leerEmailConectorLight());
    setAuthOpen(true);
  };
  useEffect(() => {
    if (!userId) return;
    let pendiente: string | null = null;
    try {
      pendiente = sessionStorage.getItem(INTENCION_SOLICITUD);
      if (pendiente === anuncioId) sessionStorage.removeItem(INTENCION_SOLICITUD);
    } catch {
      pendiente = null;
    }
    if (pendiente === anuncioId && !solicitarMut.isPending) solicitarMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, anuncioId]);

  return (
    <>
      <div className={containerCls}>
        {isLoggedOut ? (
          <Button
            type="button"
            onClick={pedirAcceso}
            className={`${btnCls} bg-primary text-primary-foreground hover:bg-primary/90`}
          >
            Solicitar más información
          </Button>
        ) : (
          <>
            {solicitado ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => cancelarMut.mutate()}
                disabled={cancelarMut.isPending}
                className={btnCls}
              >
                {cancelarMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <X className="h-4 w-4 mr-1" />}
                Cancelar solicitud
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                onClick={() => descartarMut.mutate()}
                disabled={descartarMut.isPending}
                className={btnCls}
              >
                {descartarMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <X className="h-4 w-4 mr-1" />}
                Descartar
              </Button>
            )}
            <Button
              type="button"
              onClick={() => solicitarMut.mutate()}
              disabled={solicitarMut.isPending || solicitado}
              className={`${btnCls} bg-primary text-primary-foreground hover:bg-primary/90`}
            >
              {solicitado ? (
                <><Check className="h-4 w-4 mr-1" /> Solicitud creada{fechaFmt ? ` · ${fechaFmt}` : ""}</>
              ) : solicitarMut.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Enviando…</>
              ) : (
                "Solicitar más información"
              )}
            </Button>
          </>
        )}
      </div>

      <AuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        prefillEmail={prefillEmail}
        emailTitle="Accede para enviar tu solicitud"
      />




      <Dialog open={mensajeOpen} onOpenChange={setMensajeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¡Solicitud enviada!</DialogTitle>
            <DialogDescription>
              Cuéntale al anunciante qué te interesa del local o qué necesitas saber. Cuanta más información aportes, mejor podrá preparar la respuesta.
            </DialogDescription>
            {demandaVinculada?.referencia && (
              <p className="text-xs text-muted-foreground">
                La hemos vinculado a tu búsqueda {demandaVinculada.referencia}.
              </p>
            )}
          </DialogHeader>
          {notif?.anuncianteSinEmail && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-100">
              <Phone className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-1">
                {notif.anuncianteTelefono ? (
                  <p>
                    El anunciante no dispone de correo electrónico, por lo que no recibirá esta
                    solicitud de forma automática. Para agilizar la gestión, puede contactarle
                    directamente al teléfono{" "}
                    <a href={`tel:${notif.anuncianteTelefono.replace(/\s+/g, "")}`} className="font-semibold underline underline-offset-2">
                      {notif.anuncianteTelefono}
                    </a>
                    {notif.anuncianteNombre ? ` (${notif.anuncianteNombre})` : ""}.
                  </p>
                ) : (
                  <p>
                    El anunciante no dispone de correo electrónico. Nuestro equipo gestionará su
                    solicitud y le contactará a través de otros canales.
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="mensaje-solicitud">Tu mensaje (opcional)</Label>
            <Textarea
              id="mensaje-solicitud"
              rows={5}
              placeholder="Ej: Me interesa saber si admite cambio de uso a restauración, horarios de visita disponibles, etc."
              value={mensaje}
              onChange={(e) => setMensaje(e.target.value)}
              maxLength={4000}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setMensajeOpen(false)}
              disabled={mensajeMut.isPending}
            >
              Ahora no
            </Button>
            <Button
              type="button"
              onClick={() => mensajeMut.mutate()}
              disabled={mensajeMut.isPending || !mensaje.trim() || !solicitudId}
            >
              {mensajeMut.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Guardando…</> : "Añadir a la solicitud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ConectorStickyBar({ demandaId, anuncioId, hidden }: { demandaId: string | null; anuncioId: string; hidden?: boolean }) {
  return (
    <div className={`fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-[0_-4px_20px_-8px_rgba(0,0,0,0.15)] transition-transform duration-300 ${hidden ? "translate-y-full" : "translate-y-0"}`}>
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 py-3">
        <ConectorActions demandaId={demandaId} anuncioId={anuncioId} layout="bar" />
      </div>
    </div>
  );
}
